/**
 * Where the game's Netlify Functions actually live. Relative ("/.netlify/
 * functions") on snakescore.com, where Netlify serves the static site and
 * its Functions from the same origin - the default, requiring no env var,
 * so the normal production build's behavior/URL is unchanged.
 *
 * The CrazyGames build hosts the game bundle from CrazyGames' own origin,
 * which has no such route, so it must call the real backend host
 * directly instead. `VITE_FUNCTIONS_BASE_URL` (set only in
 * `.env.crazygames` - see that file) overrides the default with the
 * absolute `https://snakescore.com/.netlify/functions`. A trailing slash
 * is stripped so `${FUNCTIONS_BASE}/${name}` never produces a doubled
 * "//" regardless of how the value is supplied.
 */
const FUNCTIONS_BASE = (import.meta.env.VITE_FUNCTIONS_BASE_URL || "/.netlify/functions").replace(/\/+$/, "");

export type FunctionCallResult<T> = { ok: true; data: T } | { ok: false; status: number; message: string };

export interface CallFunctionOptions {
  /**
   * PR28: `fetch`'s own `keepalive` flag - lets the browser keep this
   * specific request alive past the page/scene that issued it being torn
   * down (a real tab close/navigation-away), instead of the request being
   * silently aborted mid-flight. Only meaningful for a small, fire-and-
   * forget body (the platform enforces a combined ~64KB keepalive-request
   * budget) - never set this for a request whose caller needs to react to
   * the response (e.g. the RETRY SAVE flow), only for a best-effort report
   * like the Daily DNF/abandonment submission.
   */
  keepalive?: boolean;
}

/**
 * Calls one Netlify Function with the caller's Supabase access token as a
 * Bearer token - the only way any write (name, run) reaches the server;
 * the browser never talks to the `service_role`-protected tables or RPCs
 * directly. Never throws: network failures and non-2xx responses both
 * resolve to `{ ok: false }` so callers can decide retry vs. drop.
 */
export async function callFunction<T>(name: string, accessToken: string, body: unknown, options?: CallFunctionOptions): Promise<FunctionCallResult<T>> {
  try {
    const response = await fetch(`${FUNCTIONS_BASE}/${name}`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
      ...(options?.keepalive ? { keepalive: true } : {}),
    });

    let parsed: unknown;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }

    if (!response.ok) {
      const message =
        parsed && typeof parsed === "object" && typeof (parsed as Record<string, unknown>).error === "string"
          ? (parsed as Record<string, string>).error
          : "Request failed.";
      return { ok: false, status: response.status, message };
    }

    return { ok: true, data: parsed as T };
  } catch {
    return { ok: false, status: 0, message: "Network error." };
  }
}
